import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

// ---------------------------------------------------------------------------
// Tipi
// ---------------------------------------------------------------------------

export interface BusinessUnit {
  id: string
  tenant_id: string
  name: string
  code: string
  description: string | null
  color: string
  is_active: boolean
  created_at: string
  deleted_at: string | null
}

export interface BuMapping {
  business_unit_id: string
  service_lines: string[]
}

export interface BuTarget {
  id: string
  year: number
  target_revenue: string | null
  target_margin: string | null
  target_utilization: string | null
  updated_at: string | null
}

export interface CreateBusinessUnitPayload {
  name: string
  code: string
  description?: string
  color?: string
}

export interface UpdateBusinessUnitPayload {
  name?: string
  code?: string
  description?: string
  color?: string
}

export interface UpsertTargetPayload {
  target_revenue?: string
  target_margin?: string
  target_utilization?: string
}

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export function useBusinessUnits() {
  return useQuery<BusinessUnit[]>({
    queryKey: ['business-units'],
    queryFn: () => api.get<BusinessUnit[]>('/api/v1/business-units'),
    staleTime: 30_000,
    retry: false,
  })
}

export function useBusinessUnitMapping(buId: string) {
  return useQuery<BuMapping>({
    queryKey: ['business-units', buId, 'mapping'],
    queryFn: () => api.get<BuMapping>(`/api/v1/business-units/${buId}/mapping`),
    enabled: !!buId,
    staleTime: 30_000,
  })
}

export function useBusinessUnitTargets(buId: string) {
  return useQuery<BuTarget[]>({
    queryKey: ['business-units', buId, 'targets'],
    queryFn: () => api.get<BuTarget[]>(`/api/v1/business-units/${buId}/targets`),
    enabled: !!buId,
    staleTime: 30_000,
  })
}

// ---------------------------------------------------------------------------
// Mutation
// ---------------------------------------------------------------------------

export function useCreateBusinessUnit() {
  const qc = useQueryClient()
  return useMutation<BusinessUnit, Error, CreateBusinessUnitPayload>({
    mutationFn: (payload) =>
      api.post<BusinessUnit>('/api/v1/business-units', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business-units'] }),
  })
}

export function useUpdateBusinessUnit() {
  const qc = useQueryClient()
  return useMutation<BusinessUnit, Error, { id: string; payload: UpdateBusinessUnitPayload }>({
    mutationFn: ({ id, payload }) =>
      api.patch<BusinessUnit>(`/api/v1/business-units/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business-units'] }),
  })
}

export function useDeleteBusinessUnit() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/api/v1/business-units/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business-units'] }),
  })
}

export function useUpdateBusinessUnitMapping() {
  const qc = useQueryClient()
  return useMutation<BuMapping, Error, { buId: string; serviceLines: string[] }>({
    mutationFn: ({ buId, serviceLines }) =>
      api.put<BuMapping>(`/api/v1/business-units/${buId}/mapping`, { service_lines: serviceLines }),
    onSuccess: (_data, { buId }) => {
      qc.invalidateQueries({ queryKey: ['business-units', buId, 'mapping'] })
    },
  })
}

export function useUpsertBusinessUnitTargets() {
  const qc = useQueryClient()
  return useMutation<BuTarget, Error, { buId: string; year: number; payload: UpsertTargetPayload }>({
    mutationFn: ({ buId, year, payload }) =>
      api.put<BuTarget>(`/api/v1/business-units/${buId}/targets/${year}`, payload),
    onSuccess: (_data, { buId }) => {
      qc.invalidateQueries({ queryKey: ['business-units', buId, 'targets'] })
    },
  })
}
